import { RefObject, useCallback, useEffect, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import { createRoot } from "react-dom/client";
import { ROUTING_CONFIG } from "../config/constants";
import type { linkData } from "../types/routing";
import StairsPopupContent, { StairsPopupOption } from "../components/StairsPopupContent";
import type { MapLabelLanguage } from "@/utils/mapDataUtils";

type ChangeFloorHandler = (floorId: string) => Promise<void> | void;

type LinkType = "stairs" | "elevator";

type ShowPopupParams = {
  facilityId: string;
  coords: [number, number];
  linkType: LinkType;
};

type PopupEntry = {
  popup: maplibregl.Popup;
  dispose: () => void;
};

type LinkFacility = linkData["links"][number]["facilities"][number];

const parseFloorNumber = (facility: LinkFacility): number | null => {
  if (typeof facility.floorId === "number" && !Number.isNaN(facility.floorId)) {
    return facility.floorId;
  }

  const floorSegment = facility.floorId.split("-").pop();
  if (!floorSegment) {
    return null;
  }

  const match = floorSegment.match(/(\d+)/);
  if (!match) {
    return null;
  }

  const value = Number(match[1]);
  if (Number.isNaN(value)) {
    return null;
  }

  return /^b/i.test(floorSegment) ? -value : value;
};

const formatFloorLabel = (
  facility: LinkFacility,
  floorNumber: number | null,
  language: MapLabelLanguage,
): string => {
  if (floorNumber === null) {
    const fallback = facility.floorId.split("-").pop() ?? facility.floorId;
    return language === "en" ? `${fallback}` : `${fallback}階`;
  }

  if (floorNumber < 0) {
    return language === "en" ? `B${Math.abs(floorNumber)}` : `B${Math.abs(floorNumber)}階`;
  }

  if (floorNumber === 0) {
    return language === "en" ? "1F" : "1階";
  }

  return language === "en" ? `${floorNumber}F` : `${floorNumber}階`;
};

export const useStairsFloorPopup = (
  map: RefObject<maplibregl.Map | null>,
  onChangeFloor: ChangeFloorHandler,
  language: MapLabelLanguage = "ja",
) => {
  const popupRef = useRef<PopupEntry | null>(null);
  const linkDataRef = useRef<linkData | null>(null);
  const linkDataPromiseRef = useRef<Promise<linkData> | null>(null);

  const disposePopup = useCallback(() => {
    if (!popupRef.current) return;
    const { popup, dispose } = popupRef.current;
    popupRef.current = null;
    dispose();
    popup.remove();
  }, []);

  //リンクデータを取得してキャッシュする
  const ensureLinkData = useCallback(async (): Promise<linkData | null> => {
    // すでにデータがあればそれを返す
    if (linkDataRef.current) {
      return linkDataRef.current;
    }
    // まだ取得中であればそのプロミスを返す
    if (!linkDataPromiseRef.current) {
      linkDataPromiseRef.current = fetch(ROUTING_CONFIG.linkDataPath)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load link data. status=${response.status}`);
          }
          return response.json();
        })
        .then((json) => json as linkData)
        .catch((error) => {
          console.error("リンクデータの取得に失敗しました", error);
          throw error;
        });
    }

    try {
      // fetchが成功したらデータを保存
      const data = await linkDataPromiseRef.current;
      linkDataRef.current = data;
      return data;
    } catch {
      // エラー時は再試行できるようにプロミスをクリア
      linkDataPromiseRef.current = null;
      return null;
    }
  }, []);

  const buildPopupOptions = useCallback(
    (link: linkData["links"][number], facilityId: string) => {
      // 現在の施設の階数を取得
      const currentFacility = link.facilities.find(
        (facility) => facility.facilityId === facilityId,
      );
      const currentFloorNumber = currentFacility ? parseFloorNumber(currentFacility) : null;
      // 他の施設を階数順にソート（自分以外）
      const sortedFacilities = link.facilities
        .filter((facility) => facility.facilityId !== facilityId)
        .map((facility) => ({ facility, floorNumber: parseFloorNumber(facility) }))
        .sort((a, b) => {
          if (a.floorNumber === null && b.floorNumber === null) return 0;
          if (a.floorNumber === null) return 1;
          if (b.floorNumber === null) return -1;
          return a.floorNumber - b.floorNumber;
        });

      const createOption = ({ facility, floorNumber }: (typeof sortedFacilities)[number]) => {
        const direction: "up" | "down" =
          currentFloorNumber !== null && floorNumber !== null && floorNumber < currentFloorNumber
            ? "down"
            : "up";

        return {
          floorId: facility.floorId,
          floorLabel: formatFloorLabel(facility, floorNumber, language),
          direction,
        } satisfies StairsPopupOption;
      };

      if (currentFloorNumber !== null) {
        const nearestHigher = sortedFacilities.find(
          ({ floorNumber }) => floorNumber !== null && floorNumber > currentFloorNumber,
        );
        const nearestLower = [...sortedFacilities]
          .reverse()
          .find(({ floorNumber }) => floorNumber !== null && floorNumber < currentFloorNumber);

        const prioritizedOptions: StairsPopupOption[] = [];
        if (nearestHigher) {
          prioritizedOptions.push(createOption(nearestHigher));
        }
        if (nearestLower) {
          prioritizedOptions.push(createOption(nearestLower));
        }

        if (prioritizedOptions.length > 0) {
          return prioritizedOptions;
        }
      }

      // fallback: 現在階が特定できない場合や候補がない場合は従来通り全件表示
      return sortedFacilities.map(createOption);
    },
    [language],
  );
  // 階段ポップアップを表示する
  const showStairsPopup = useCallback(
    async ({ facilityId, coords, linkType }: ShowPopupParams) => {
      if (!map.current) return;

      const data = await ensureLinkData();
      if (!data) return;

      const link = data.links.find((linkItem) =>
        linkItem.facilities.some((facility) => facility.facilityId === facilityId),
      );

      if (!link) {
        disposePopup();
        return;
      }
      // ポップアップの選択肢を構築
      const options = buildPopupOptions(link, facilityId);

      if (options.length === 0) {
        disposePopup();
        return;
      }

      disposePopup();

      const container = document.createElement("div");
      const root = createRoot(container);
      let isUnmounted = false;

      const handleSelect = async (floorId: string) => {
        disposePopup();
        await onChangeFloor(floorId);
      };

      const dispose = () => {
        if (isUnmounted) return;
        isUnmounted = true;

        // React 18 では別ルートのアンマウントを現在のレンダリング中に同期実行できないため、
        // マイクロタスクに退避してから unmount する。
        const scheduleUnmount = () => {
          if (typeof queueMicrotask === "function") {
            queueMicrotask(() => root.unmount());
          } else {
            setTimeout(() => root.unmount(), 0);
          }
        };

        scheduleUnmount();
      };
      // ポップアップコンテンツをレンダリング
      root.render(
        <StairsPopupContent options={options} onSelect={handleSelect} linkType={linkType} />,
      );

      const popup = new maplibregl.Popup({
        closeButton: false,
        offset: 12,
        className: "stairs-popup",
        maxWidth: "240px",
      })
        .setDOMContent(container)
        .setLngLat(coords)
        .addTo(map.current);

      popup.on("close", () => {
        if (popupRef.current?.popup === popup) {
          popupRef.current = null;
        }
        dispose();
      });

      popupRef.current = { popup, dispose };
    },
    [buildPopupOptions, disposePopup, ensureLinkData, map, onChangeFloor],
  );

  useEffect(() => disposePopup, [disposePopup]);

  return {
    showStairsPopup,
    hideStairsPopup: disposePopup,
  };
};
